import { useTranslation } from 'react-i18next';
import { useToolStore } from '../Tool/tool.store';
import { Message, useAIStore } from './ai-store';
import { CloseIcon } from '../Icons/CloseIcon';
import Markdown, { Components } from 'react-markdown';

const markdownComponents: Components = {
    ul: ({ children, ...props }) => (
        <ul className="list-disc list-outside space-y-1 my-2 ml-6 mb-4" {...props}>
            {children}
        </ul>
    ),
    ol: ({ children, ...props }) => (
        <ol className="list-decimal list-outside space-y-1 my-2 ml-6" {...props}>
            {children}
        </ol>
    ),
    li: ({ children, ...props }) => (
        <li {...props}>
            {children}
        </li>
    ),
};

export function AIPanel() {
    const mode = useAIStore((state) => state.mode);
    const messages = useAIStore((state) => state.messages);
    const startCorrelationAnalysis = useAIStore(
        (state) => state.startCorrelationAnalysis
    );
    const startRegressionAnalysis = useAIStore(
        (state) => state.startRegressionAnalysis
    );
    const reset = useAIStore((state) => state.reset);
    const setShouldShowAIPanel = useAIStore(
        (state) => state.setShouldShowAIPanel
    );

    const { t, i18n } = useTranslation();

    const indicators = useToolStore((state) => state.indicators);
    const featureFilter = useToolStore((state) => state.featureFilter);
    const geoLevel = useToolStore((state) => state.geoLevel);
    const isDone = mode.endsWith('done');
    const indicatorsMap = useToolStore((state) => state.indicatorsMap);
    const timeRangeMap = useToolStore((state) => state.timeRangeMap);

    const indicatorNames = Object.entries(indicatorsMap).reduce(
        (acc, [id, indicator]) => {
            acc[id] = `${indicator.label[i18n.language]} (${
                indicator.subLabel[i18n.language]
            })`;
            return acc;
        },
        {} as Record<string, string>
    );

    // Calculate the common time range for the selected indicators
    const getTimeRange = () => {
        if (!indicators?.x || !indicators?.y || !geoLevel || !timeRangeMap) {
            return { minYear: '', maxYear: '' };
        }
        const xRange = timeRangeMap[indicators.x]?.[geoLevel]?.timeRange;
        const yRange = timeRangeMap[indicators.y]?.[geoLevel]?.timeRange;
        if (!xRange || !yRange) {
            return { minYear: '', maxYear: '' };
        }
        return {
            minYear: Math.max(xRange[0], yRange[0]),
            maxYear: Math.min(xRange[1], yRange[1]),
        };
    };
    const { minYear, maxYear } = getTimeRange();

    // Get header text based on current mode
    const getHeaderText = () => {
        if (mode === 'idle') {
            return t('ai.title');
        } else if (mode.startsWith('correlation-analysis')) {
            return t('ai.title-correlation');
        } else {
            return t('ai.title');
        }
    };

    const showMessages =
        mode.startsWith('correlation-analysis') ||
        mode.startsWith('regression-analysis');

    return (
        <section className="card bg-base-100 text-base-content p-0 h-full">
            <div className="flex items-center gap-2 mb-2 px-3 pt-3">
                {mode !== 'idle' && (
                    <button
                        className="btn btn-xs btn-ghost"
                        onClick={() => reset()}
                    >
                        ←
                    </button>
                )}
                <h1>{getHeaderText()}</h1>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
                <div className="absolute top-2 right-2">
                    <button
                        onClick={() => setShouldShowAIPanel(false)}
                        className="btn btn-xs btn-circle btn-ghost"
                    >
                        <CloseIcon />
                    </button>
                </div>
                {/* Buttons for choosing analysis type. */}
                {mode === 'idle' && (
                    <>
                        <p className="text-sm mb-4 whitespace-pre-line">
                            {t('ai.explanation')}
                        </p>
                        <h2 className="mb-2">{t('ai.tools')}</h2>
                        <div className="flex gap-2 flex-col items-start">
                            <div>
                                <button
                                    className="btn btn-md btn-primary"
                                    onClick={() =>
                                        startCorrelationAnalysis(
                                            indicators!.y,
                                            geoLevel,
                                            featureFilter,
                                            indicatorNames
                                        )
                                    }
                                >
                                    {t('ai.correlation-analysis')}
                                </button>
                                <p className="pl-3 text-sm">
                                    <i>
                                        {t('ai.correlation-description')}
                                    </i>
                                </p>
                            </div>
                            <div>
                                <button
                                    className="btn btn-md btn-primary"
                                    onClick={() =>
                                        startRegressionAnalysis(
                                            indicators!.y,
                                            [indicators!.x],
                                            geoLevel,
                                            featureFilter,
                                            indicatorNames
                                        )
                                    }
                                >
                                    {t('ai.regression-analysis')}
                                </button>
                                <p className="pl-3 text-sm">
                                    <i>
                                        {t('ai.regression-description', { minYear, maxYear })}
                                    </i>
                                </p>
                            </div>
                        </div>
                    </>
                )}

                {/* Show messages from system. */}
                {showMessages && (
                    <div className="group flex flex-col gap-4 max-w-lg">
                        <MessageList messages={messages} isDone={isDone} />
                        <div className="text-xs px-4 text-center text-primary">
                            <p>
                                {t('ai.disclaimer')}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

function MessageList(props: { messages: Message[]; isDone: boolean }) {
    const { t, i18n } = useTranslation();
    const indicatorsMap = useToolStore((state) => state.indicatorsMap);
    const setIndicatorById = useToolStore((state) => state.setIndicatorById);

    return (
        <>
            {props.messages.map((message, i) => {
                const hasIndicatorIds =
                    message.indicatorIdsWithCorrelation &&
                    message.indicatorIdsWithCorrelation.length > 0;

                return (
                    <div
                        key={message.id}
                        className="flex flex-col gap-2 w-full"
                    >
                        <div className="flex items-start gap-2 w-full">
                            {!props.isDone &&
                            i === props.messages.length - 1 ? (
                                <span className="loading loading-spinner loading-xs text-secondary"></span>
                            ) : (
                                <div className="badge badge-xs badge-primary"></div>
                            )}
                            <div className="-mt-1.5 w-full">
                                {message.type === 'message' && (
                                    <div className="">
                                        <Markdown components={markdownComponents}>
                                            {message.text}
                                        </Markdown>
                                        {message.additionalText && (
                                            <div className="text-sm">
                                                <Markdown
                                                    components={markdownComponents}
                                                >
                                                    {message.additionalText}
                                                </Markdown>
                                            </div>
                                        )}

                                        {message.details && (
                                            <details className="mt-4 text-sm">
                                                <summary className="cursor-pointer">
                                                    {t('ai.technical-details')}
                                                </summary>
                                                <div className="whitespace-pre-line">
                                                    {message.details}
                                                </div>
                                            </details>
                                        )}
                                    </div>
                                )}
                                {message.type === 'error' && (
                                    <div className="text-error">
                                        {message.text}
                                    </div>
                                )}
                            </div>
                        </div>
                        {hasIndicatorIds && (
                            <div className="flex flex-col gap-2 ml-4 w-full max-w-[calc(100%-1rem)]">
                                {message
                                    .indicatorIdsWithCorrelation!.filter(
                                        (d) => indicatorsMap[d.id]
                                    )
                                    .map((d) => (
                                        <button
                                            key={d.id}
                                            className="btn btn-xs btn-primary normal-case text-left whitespace-normal h-auto min-h-6 break-words w-full p-1"
                                            style={{ wordBreak: 'break-word' }}
                                            onClick={() => {
                                                setIndicatorById({
                                                    indicatorId: d.id,
                                                    key: 'x',
                                                });
                                            }}
                                        >
                                            <div className="flex items-center gap-2 w-full">
                                                <span className="font-mono whitespace-nowrap shrink-0">
                                                    {d.correlation.toFixed(2)}:
                                                </span>
                                                <span className="flex-1">
                                                    {
                                                        indicatorsMap[d.id]
                                                            .label[
                                                            i18n.language
                                                        ]
                                                    }
                                                    ,{' '}
                                                    {
                                                        indicatorsMap[d.id]
                                                            .subLabel[
                                                            i18n.language
                                                        ]
                                                    }
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );
}
